import { RegistryForm } from 'app/src/features/UserSelfRegistration/components/RegistryForm'
import { useUserSelfRegistration } from 'app/src/services/welfareportal/membership-api'
import type * as Schemas from 'app/src/services/welfareportal/membership-api/membership-schemas'
import { Image } from 'app/src/shared/components'
import { setToSessionStorage } from 'app/src/shared/helpers'
import { useWebAppContext, useWelfarePortalNavigate } from 'app/src/shared/hooks'
import pws from 'app/static/images/pws.png'
import { Button } from 'primereact/button'
import { Toast } from 'primereact/toast'
import { ReactElement, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function UserSelfRegistration(): ReactElement {
  const { t } = useTranslation('selfRegistration', { keyPrefix: 'SelfRegistration' })
  const { t: tHome } = useTranslation('home', { keyPrefix: 'AppBar' })
  const context = useWebAppContext()
  const toastRef = useRef<Toast>(null)
  const navigate = useWelfarePortalNavigate()
  const [disableRegisterButton, setDisableRegisterButton] = useState<boolean>(true)
  const [userSelfRegistration, setUserSelfRegistration] = useState<Schemas.UserSelfRegistration | undefined>(undefined)

  const handleSelfUserRegistration = useCallback((isValid: boolean, user?: Schemas.UserSelfRegistration) => {
    setDisableRegisterButton(!isValid)
    if (isValid) {
      setUserSelfRegistration(user)
    }
  }, [])

  const userRegistrationMutation = useUserSelfRegistration({
    onSuccess: async () => {
      // Mostra un messaggio di successo
      toastRef.current?.show({
        severity: 'success',
        summary: t('Messages.SummarySuccess'),
        detail: t('Messages.DetailSuccess'),
        life: 5000,
      })

      // prendi le informazioni dell'utente e popola il context
      try {
        // Chiamata all'API /info per ottenere i dati aggiornati dell'utente
        const userInfoResponse = await context.httpClient?.executeJson({
          input: 'membership/api/v1/users/info',
        })

        if (userInfoResponse?.jsonResponse) {
          // Aggiorna il contesto con le informazioni complete dell'utente
          // Assicurati che tutti i campi di UserInfo siano presenti
          context?.update({
            userInfo: {
              ...context.userInfo, // Mantieni i campi esistenti
              ...userInfoResponse.jsonResponse, // Sovrascrivi con i nuovi dati
              // Imposta esplicitamente i flag di stato
              canSelfRegister: false,
              isForbidden: false, // Dopo la registrazione, l'utente non è proibito
            },
          })

          if ((userInfoResponse.jsonResponse as Schemas.UserInfo).userMemberships[0]) {
            setToSessionStorage(
              'selectedCampaign',
              JSON.stringify((userInfoResponse.jsonResponse as Schemas.UserInfo).userMemberships[0]),
            )
          }
        } else {
          // Se non riceviamo info, aggiorna almeno il flag canSelfRegister
          context?.update({ userInfo: { ...context.userInfo, canSelfRegister: false } })
        }
      } catch (error) {
        console.error('Error fetching updated user info:', error)
        // In caso di errore, aggiorna almeno il flag canSelfRegister
        context?.update({ userInfo: { ...context.userInfo, canSelfRegister: false } })
      }

      navigate.goToLanding()
      window.location.reload() //TODO: remove after testing
    },
    onError: (error) => {
      // Mostra un messaggio di errore
      toastRef.current?.show({
        severity: 'error',
        summary: t('Messages.SummaryError'),
        detail: error.status === 409 ? t('Messages.DuplicatedUserError') : t('Messages.GenericError'),
        life: 5000,
      })
    },
  })

  const handleRegister = async () => {
    if (!userSelfRegistration) return

    try {
      const {
        userEmail = '',
        firstName = '',
        lastName = '',
        userBirthDate = '',
        fiscalCode = '',
        userGender = 'M',
      } = userSelfRegistration

      userRegistrationMutation.mutate({
        body: {
          userEmail,
          firstName,
          lastName,
          userBirthDate,
          fiscalCode,
          userGender: (userGender as 'M' | 'F') ?? 'M',
        },
      })
    } catch (error) {
      console.error('Errore durante la registrazione:', error)
    }
  }

  return (
    <>
      <Toast ref={toastRef} position='top-center' />
      <nav>
        <div className='noPrint bg-[var(--card-background)] py-4 pl-2 lg:pl-4 xl:pl-8'>
          <div className='mx-auto flex max-w-[var(--max-application-size)] items-center justify-between'>
            <span className='grid place-content-center gap-4 md:ml-10'>
              <Image src={pws} alt={tHome('AltLogoImage')} pt={{ image: { className: 'mr-0 md:mr-4 h-6' } }} />
            </span>
            <span className='pr-2 lg:pr-4 xl:pr-8'>
              {context.portalConfiguration.preloginApplicationUri && (
                <button
                  aria-label='close button'
                  className='flex items-center'
                  onClick={() => window.open(context.portalConfiguration.preloginApplicationUri)}
                >
                  <span className='pi pi-times p-2' />
                  <span className='hidden md:block'>{t('CloseBtn')}</span>
                </button>
              )}
            </span>
          </div>
        </div>
      </nav>
      <main>
        <div className='flex min-h-lvh flex-col'>
          <div className='bg-white pl-2 lg:pl-4 xl:pl-8'>
            <div className='mx-auto flex max-w-[var(--max-application-size)] items-center justify-between'>
              <span className='my-6 md:ml-10'>
                <h1 className='!text-4xl font-bold lg:!text-4xl'>{t('Title')}</h1>
              </span>
            </div>
          </div>
          <div className='flex flex-1 items-stretch bg-[#EFF3FF]'>
            <div className='mx-auto flex w-full max-w-[var(--max-application-size)] items-center justify-between'>
              <span className='h-full w-full py-6 md:ml-10'>
                <div className='flex h-full flex-col justify-between gap-6 md:px-4'>
                  <div className='flex-1 overflow-y-auto bg-white p-2 md:p-8'>
                    <RegistryForm onValidatingForm={handleSelfUserRegistration} />
                  </div>
                  <div className='sticky bottom-0 flex flex-col items-center justify-end gap-2 border-t-2 border-[#0047BB] bg-white p-2 md:flex-row md:px-6'>
                    <Button
                      disabled={disableRegisterButton || userRegistrationMutation.isPending}
                      label={t('RegisterBtn')}
                      rounded
                      onClick={handleRegister}
                      loading={userRegistrationMutation.isPending}
                    />
                  </div>
                </div>
              </span>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
